const serverURL = import.meta.env.VITE_API_URL

export interface FeedbackData {
  scan_result_id: number | null
  org_uuid?: string
  subject: string
  category?: string
  bad_match_category?: string
  message: string
  hidden: boolean
  consent_to_share?: boolean
  token?: string
}

export interface FeedbackResponse {
  id: number
  scan_result_id: number | null
  subject: string
  message: string
  user_uuid: string
  status: string
  created_at: string
  updated_at: string
  hidden: boolean
}

export interface HelpFormData {
  organisationName?: string
  orgUuid?: string
  userName?: string
  email?: string
  phone?: string
  description: string
}

export interface OpportunitySuggetionFormData {
  organisationName?: string
  orgUuid?: string
  userName?: string
  email?: string
  fundingNeedTitle: string
  description: string
}

export interface PreferenceModification {
  preference_id: number
  original_text: string
  suggested_text: string
  need_id: number | null
}

export interface OrgMatchPreferenceSuggestion {
  preference_suggestions: string[]
  preference_modifications: PreferenceModification[]
  reasoning: string
}

interface FeedbackCreatedResponse {
  success: boolean
  preference_response?: OrgMatchPreferenceSuggestion
}

/**
 * Submit feedback for a scan result
 */
export const submitFeedback = async (
  feedback: FeedbackData,
): Promise<FeedbackCreatedResponse> => {
  // Extract token and create clean feedback object without token
  const { token, ...feedbackData } = feedback

  // Check if serverURL already contains /v1/client
  const baseUrl = serverURL.endsWith('/') ? serverURL.slice(0, -1) : serverURL
  const endpoint = baseUrl.includes('/v1/client')
    ? 'feedback'
    : '/v1/client/feedback'

  const response = await fetch(`${baseUrl}/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(feedbackData),
  })

  if (!response.ok) {
    throw new Error('Failed to submit feedback')
  }

  return response.json()
}

/**
 * Update the hidden status of a feedback
 */
export const updateFeedbackHidden = async (
  feedbackId: number,
  hidden: boolean,
  token: string,
): Promise<FeedbackResponse> => {
  // Check if serverURL already contains /v1/client
  const baseUrl = serverURL.endsWith('/') ? serverURL.slice(0, -1) : serverURL
  const endpoint = baseUrl.includes('/v1/client')
    ? `feedback/${feedbackId}`
    : `/v1/client/feedback/${feedbackId}`

  const response = await fetch(`${baseUrl}/${endpoint}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ hidden }),
  })

  if (!response.ok) {
    throw new Error('Failed to update feedback')
  }

  return response.json()
}

export async function submitHelpRequest(
  helpRequest: HelpFormData,
  contactType: string,
  token: string,
): Promise<{ message: string }> {
  const response = await fetch(`${serverURL}/feedback/help`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...helpRequest, contactType }),
  })

  if (!response.ok) {
    throw new Error('Failed to submit request')
  }

  return response.json()
}

export async function submitOppSuggestionRequest(
  oppSuggestion: OpportunitySuggetionFormData,
  token: string,
): Promise<{ message: string }> {
  const response = await fetch(`${serverURL}/feedback/opp-suggestion`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...oppSuggestion }),
  })

  if (!response.ok) {
    throw new Error('Failed to submit feedback')
  }

  return response.json()
}
